import React, { useState, useEffect, useMemo } from "react";
import {
  IconX,
  IconPlus,
  IconMoreVertical,
  IconArrowLeft,
  IconTrending,
} from "../components/icons";
import { EXCHANGE_RATE } from "../config/constants";
import { formatTime } from "../utils/helpers";

const InvestmentModal = ({
  form,
  setForm,
  handleSubmit,
  setShowModal,
  loading,
}) => (
  <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[80] p-4 backdrop-blur-sm">
    <div className="bg-[#1E2130] w-full max-w-md p-6 rounded-3xl border border-slate-700 shadow-2xl max-h-[90vh] overflow-y-auto">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-bold text-white">
          {form.id ? "Edit Transaction" : "New Transaction"}
        </h3>
        <button
          onClick={() => setShowModal(false)}
          className="text-slate-400 hover:text-white"
        >
          <IconX />
        </button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] text-slate-400 ml-1">Type</label>
            <select
              className="w-full bg-[#262A3B] p-3 rounded-xl text-white outline-none border border-slate-700"
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
            >
              <option value="Buy">Buy</option>
              <option value="Sell">Sell</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] text-slate-400 ml-1">Date</label>
            <input
              type="datetime-local"
              className="w-full bg-[#262A3B] p-3 rounded-xl text-white outline-none border border-slate-700 scheme-dark"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
            />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <label className="text-[10px] text-slate-400 ml-1">
              Asset Name
            </label>
            <input
              type="text"
              className="w-full bg-[#262A3B] p-3 rounded-xl text-white outline-none border border-slate-700 uppercase"
              placeholder="BTC"
              value={form.assetName}
              onChange={(e) =>
                setForm({ ...form, assetName: e.target.value.toUpperCase() })
              }
              required
            />
          </div>
          <div>
            <label className="text-[10px] text-slate-400 ml-1">Currency</label>
            <select
              className="w-full bg-[#262A3B] p-3 rounded-xl text-white outline-none border border-slate-700"
              value={form.currency}
              onChange={(e) => setForm({ ...form, currency: e.target.value })}
            >
              <option>THB</option>
              <option>USD</option>
              <option>USDT</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] text-slate-400 ml-1">
              Price / Unit
            </label>
            <input
              type="number"
              step="0.000001"
              className="w-full bg-[#262A3B] p-3 rounded-xl text-white outline-none border border-slate-700"
              value={form.price}
              onChange={(e) => setForm({ ...form, price: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="text-[10px] text-slate-400 ml-1">Quantity</label>
            <input
              type="number"
              step="0.00000001"
              className="w-full bg-[#262A3B] p-3 rounded-xl text-white outline-none border border-slate-700"
              value={form.quantity}
              onChange={(e) => setForm({ ...form, quantity: e.target.value })}
              required
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] text-slate-400 ml-1">Fees</label>
            <input
              type="number"
              step="0.01"
              className="w-full bg-[#262A3B] p-3 rounded-xl text-white outline-none border border-slate-700"
              value={form.fees}
              onChange={(e) => setForm({ ...form, fees: e.target.value })}
            />
          </div>
          <div>
            <label className="text-[10px] text-slate-400 ml-1">Platform</label>
            <input
              type="text"
              className="w-full bg-[#262A3B] p-3 rounded-xl text-white outline-none border border-slate-700"
              value={form.platform}
              onChange={(e) => setForm({ ...form, platform: e.target.value })}
              placeholder="Binance"
            />
          </div>
        </div>
        <input
          type="text"
          className="w-full bg-[#262A3B] p-3 rounded-xl text-white outline-none border border-slate-700"
          placeholder="Note (Optional)"
          value={form.note}
          onChange={(e) => setForm({ ...form, note: e.target.value })}
        />
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-3 rounded-xl shadow-lg mt-4"
        >
          {loading ? "Saving..." : "Save Transaction"}
        </button>
      </form>
    </div>
  </div>
);

export default function InvestmentPage({ apiUrl }) {
  const [investments, setInvestments] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [displayCurrency, setDisplayCurrency] = useState("THB");
  const [form, setForm] = useState({
    id: null,
    assetName: "",
    platform: "",
    type: "Buy",
    price: "",
    quantity: "",
    fees: "",
    currency: "THB",
    date: new Date().toISOString().slice(0, 16),
    note: "",
  });

  const fetchInvestments = async () => {
    try {
      const res = await fetch(`${apiUrl}?action=getInvestments`);
      const json = await res.json();
      if (json.status === "success") setInvestments(json.data);
    } catch (error) {
      console.error(error);
    }
  };
  useEffect(() => {
    fetchInvestments();
  }, []);

  const convertValue = (amount, fromCurrency, toCurrency) => {
    const val = parseFloat(amount || 0);
    if (fromCurrency === toCurrency) return val;
    let amountInTHB = val;
    if (fromCurrency === "USD" || fromCurrency === "USDT")
      amountInTHB = val * EXCHANGE_RATE;
    if (toCurrency === "THB") return amountInTHB;
    if (toCurrency === "USD" || toCurrency === "USDT")
      return amountInTHB / EXCHANGE_RATE;
    return val;
  };
  const getSymbol = (curr) => (curr === "THB" ? "฿" : "$");

  const portfolio = useMemo(() => {
    const assets = {};
    const sorted = [...investments].sort(
      (a, b) => new Date(a.Date) - new Date(b.Date)
    );
    sorted.forEach((t) => {
      const qty = parseFloat(t.Quantity || 0);
      const fees = convertValue(t.Fees, t.Currency, displayCurrency);
      const price = convertValue(t.Price, t.Currency, displayCurrency);
      const key = (t.AssetName || "UNKNOWN").toUpperCase();

      if (!assets[key])
        assets[key] = {
          symbol: key,
          quantity: 0,
          totalCost: 0,
          platform: t.Platform,
        };
      if (t.Type === "Buy") {
        assets[key].quantity += qty;
        assets[key].totalCost += price * qty + fees;
      } else {
        const avgCostPerUnit =
          assets[key].quantity > 0
            ? assets[key].totalCost / assets[key].quantity
            : 0;
        assets[key].totalCost -= avgCostPerUnit * qty;
        assets[key].quantity -= qty;
      }
      if (assets[key].quantity <= 0.00000001) {
        assets[key].quantity = 0;
        assets[key].totalCost = 0;
      }
    });
    return Object.values(assets).filter((a) => a.quantity > 0);
  }, [investments, displayCurrency]);

  const totalPortfolioValue = portfolio.reduce(
    (sum, a) => sum + a.totalCost,
    0
  );

  const assetDetails = useMemo(() => {
    if (!selectedAsset) return null;
    const transactions = investments
      .filter((t) => t.AssetName?.toUpperCase() === selectedAsset.symbol)
      .sort((a, b) => new Date(b.Date) - new Date(a.Date));
    let buyQ = 0,
      buyC = 0,
      sellQ = 0,
      sellV = 0;
    transactions.forEach((t) => {
      const qty = parseFloat(t.Quantity || 0);
      const price = convertValue(t.Price, t.Currency, displayCurrency);
      const fees = convertValue(t.Fees, t.Currency, displayCurrency);
      if (t.Type === "Buy") {
        buyQ += qty;
        buyC += price * qty + fees;
      } else {
        sellQ += qty;
        sellV += price * qty - fees;
      }
    });
    const avgBuyPrice = buyQ > 0 ? buyC / buyQ : 0;
    const avgSellPrice = sellQ > 0 ? sellV / sellQ : 0;
    return {
      symbol: selectedAsset.symbol,
      currentQty: selectedAsset.quantity,
      currentCost: selectedAsset.totalCost,
      avgBuyPrice,
      avgSellPrice,
      transactions,
    };
  }, [selectedAsset, investments, displayCurrency]);

  const handleOpenAddModal = (name = "") => {
    setForm({
      id: null,
      assetName: name,
      platform: "",
      type: "Buy",
      price: "",
      quantity: "",
      fees: "",
      currency: displayCurrency,
      date: new Date().toISOString().slice(0, 16),
      note: "",
    });
    setShowModal(true);
  };

  const handleEdit = (t) => {
    setForm({
      id: t.ID,
      assetName: t.AssetName,
      platform: t.Platform,
      type: t.Type,
      price: t.Price,
      quantity: t.Quantity,
      fees: t.Fees,
      currency: t.Currency,
      date: t.Date ? new Date(t.Date).toISOString().slice(0, 16) : "",
      note: t.Note,
    });
    setShowModal(true);
    setMenuOpenId(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete?")) return;
    setLoading(true);
    await fetch(apiUrl, {
      method: "POST",
      body: JSON.stringify({ action: "deleteInvestment", id }),
    });
    setMenuOpenId(null);
    fetchInvestments();
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await fetch(apiUrl, {
      method: "POST",
      body: JSON.stringify({
        action: form.id ? "updateInvestment" : "addInvestment",
        ...form,
      }),
    });
    setShowModal(false);
    fetchInvestments();
    setLoading(false);
  };

  if (selectedAsset && assetDetails) {
    return (
      <div className="pb-24 animate-fadeIn pt-2 px-2">
        <div className="flex items-center justify-between mb-6 pt-2">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSelectedAsset(null)}
              className="p-2 bg-slate-800 rounded-full hover:bg-slate-700"
            >
              <IconArrowLeft size={20} />
            </button>
            <h2 className="text-2xl font-bold text-white">
              {assetDetails.symbol}
            </h2>
          </div>
          <div className="bg-slate-800 p-1 rounded-lg flex">
            <button
              onClick={() => setDisplayCurrency("THB")}
              className={`px-3 py-1 text-xs font-bold rounded-md transition ${
                displayCurrency === "THB"
                  ? "bg-indigo-600 text-white"
                  : "text-slate-400"
              }`}
            >
              THB
            </button>
            <button
              onClick={() => setDisplayCurrency("USD")}
              className={`px-3 py-1 text-xs font-bold rounded-md transition ${
                displayCurrency === "USD"
                  ? "bg-indigo-600 text-white"
                  : "text-slate-400"
              }`}
            >
              USD
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-6">
          <div className="bg-[#1E2130] p-4 rounded-xl border border-slate-700">
            <p className="text-xs text-slate-400">Holdings</p>
            <p className="text-lg font-bold text-white">
              {assetDetails.currentQty.toLocaleString(undefined, {
                maximumFractionDigits: 6,
              })}
            </p>
          </div>
          <div className="bg-[#1E2130] p-4 rounded-xl border border-slate-700">
            <p className="text-xs text-slate-400">Cost ({displayCurrency})</p>
            <p className="text-lg font-bold text-white">
              {getSymbol(displayCurrency)}
              {assetDetails.currentCost.toLocaleString(undefined, {
                maximumFractionDigits: 2,
              })}
            </p>
          </div>
          <div className="bg-[#1E2130] p-4 rounded-xl border border-slate-700">
            <p className="text-xs text-slate-400">Avg Buy</p>
            <p className="text-md font-bold text-emerald-400">
              {getSymbol(displayCurrency)}
              {assetDetails.avgBuyPrice.toLocaleString(undefined, {
                maximumFractionDigits: 4,
              })}
            </p>
          </div>
          <div className="bg-[#1E2130] p-4 rounded-xl border border-slate-700">
            <p className="text-xs text-slate-400">Avg Sell</p>
            <p className="text-md font-bold text-red-400">
              {getSymbol(displayCurrency)}
              {assetDetails.avgSellPrice.toLocaleString(undefined, {
                maximumFractionDigits: 4,
              })}
            </p>
          </div>
        </div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-white font-bold">Transactions</h3>
          <button
            onClick={() => handleOpenAddModal(assetDetails.symbol)}
            className="flex items-center gap-1 bg-slate-800 px-3 py-1.5 rounded-full text-xs font-bold text-emerald-400 border border-slate-700 hover:bg-slate-700"
          >
            <IconPlus size={14} /> Add
          </button>
        </div>
        <div className="space-y-3">
          {assetDetails.transactions.map((t) => (
            <div
              key={t.ID}
              className="bg-[#1E2130] p-4 rounded-xl border border-slate-800 relative flex flex-col gap-2"
            >
              <div className="flex justify-between items-center">
                <span
                  className={`text-[10px] font-bold px-2 py-1 rounded ${
                    t.Type === "Buy"
                      ? "bg-emerald-500/20 text-emerald-400"
                      : "bg-red-500/20 text-red-400"
                  }`}
                >
                  {t.Type}
                </span>
                <div className="flex gap-2 items-center">
                  <span className="text-[10px] text-slate-500">
                    {formatTime(t.Date)}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuOpenId(menuOpenId === t.ID ? null : t.ID);
                    }}
                    className="text-slate-500 hover:text-white relative p-1"
                  >
                    <IconMoreVertical size={16} />
                  </button>
                </div>
              </div>
              {menuOpenId === t.ID && (
                <div className="absolute right-4 top-8 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-20 w-32 overflow-hidden">
                  <button
                    onClick={() => handleEdit(t)}
                    className="w-full text-left px-4 py-3 text-xs text-white hover:bg-slate-700 border-b border-slate-700"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(t.ID)}
                    className="w-full text-left px-4 py-3 text-xs text-red-400 hover:bg-slate-700"
                  >
                    Delete
                  </button>
                </div>
              )}
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-[10px] text-slate-400">
                    Price ({t.Currency})
                  </p>
                  <p className="text-sm font-bold text-white">
                    {parseFloat(t.Price).toLocaleString()}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] text-slate-400">Amount</p>
                  <p className="text-sm font-bold text-white">
                    {parseFloat(t.Quantity).toLocaleString()}
                  </p>
                </div>
              </div>
              <div className="border-t border-slate-700/50 pt-2 mt-1 flex justify-between items-center">
                <p className="text-[10px] text-slate-500">Total (inc. fees)</p>
                <p className="text-sm font-bold text-slate-300">
                  {(
                    parseFloat(t.Price) * parseFloat(t.Quantity) +
                    parseFloat(t.Fees || 0)
                  ).toLocaleString()}{" "}
                  {t.Currency}
                </p>
              </div>
            </div>
          ))}
        </div>
        {showModal && (
          <InvestmentModal
            form={form}
            setForm={setForm}
            handleSubmit={handleSubmit}
            setShowModal={setShowModal}
            loading={loading}
          />
        )}
      </div>
    );
  }

  return (
    <div className="pb-24 animate-fadeIn p-2">
      <div className="bg-[#1E2130] p-6 rounded-3xl shadow-xl border border-slate-700 mb-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 p-4 opacity-10">
          <IconTrending size={80} />
        </div>
        <div className="flex justify-between items-start mb-2">
          <p className="text-slate-400 text-sm">
            Total Cost ({displayCurrency})
          </p>
          <div className="bg-slate-800 p-1 rounded-lg flex z-10">
            <button
              onClick={() => setDisplayCurrency("THB")}
              className={`px-3 py-1 text-xs font-bold rounded-md transition ${
                displayCurrency === "THB"
                  ? "bg-indigo-600 text-white"
                  : "text-slate-400"
              }`}
            >
              THB
            </button>
            <button
              onClick={() => setDisplayCurrency("USD")}
              className={`px-3 py-1 text-xs font-bold rounded-md transition ${
                displayCurrency === "USD"
                  ? "bg-indigo-600 text-white"
                  : "text-slate-400"
              }`}
            >
              USD
            </button>
          </div>
        </div>
        <h2 className="text-4xl font-bold text-white">
          {getSymbol(displayCurrency)}
          {totalPortfolioValue.toLocaleString(undefined, {
            maximumFractionDigits: 2,
          })}
        </h2>
      </div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-white font-bold text-lg">Assets</h3>
        <button
          onClick={() => handleOpenAddModal()}
          className="bg-indigo-600 text-white px-4 py-2 rounded-xl text-sm font-bold shadow-lg transition"
        >
          + Transaction
        </button>
      </div>
      <div className="space-y-3">
        {portfolio.map((item, i) => (
          <div
            key={i}
            onClick={() => setSelectedAsset(item)}
            className="flex justify-between items-center bg-[#1E2130] p-4 rounded-xl border border-slate-800 hover:bg-slate-700/50 cursor-pointer transition active:scale-95"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-indigo-500/20 text-indigo-400 flex items-center justify-center font-bold">
                {item.symbol[0]}
              </div>
              <div>
                <h4 className="text-white font-bold">{item.symbol}</h4>
                <p className="text-[10px] text-slate-400">{item.platform}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-white font-bold">
                {getSymbol(displayCurrency)}
                {item.totalCost.toLocaleString(undefined, {
                  maximumFractionDigits: 0,
                })}
              </p>
              <p className="text-[10px] text-slate-400">
                {item.quantity.toLocaleString()} units
              </p>
            </div>
          </div>
        ))}
      </div>
      {showModal && (
        <InvestmentModal
          form={form}
          setForm={setForm}
          handleSubmit={handleSubmit}
          setShowModal={setShowModal}
          loading={loading}
        />
      )}
    </div>
  );
}
